import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, CheckCircle2, FileSignature, Trash2, Search } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsBell({ testId = "notifications-bell" }) {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [nq, setNq] = useState("");
  const [visible, setVisible] = useState(8);
  const navigate = useNavigate();

  const refreshCount = useCallback(() => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {}), []);
  useEffect(() => {
    refreshCount();
    const token = localStorage.getItem("cp_token");
    let es, poll;
    const startPolling = () => { if (!poll) poll = setInterval(refreshCount, 20000); };
    if (token && typeof EventSource !== "undefined") {
      try {
        es = new EventSource(`${API}/notifications/stream?auth=${encodeURIComponent(token)}`);
        es.onmessage = (e) => {
          try { const d = JSON.parse(e.data); if (typeof d.count === "number") setCount(d.count); } catch { /* heartbeat */ }
        };
        es.onerror = () => { if (es) es.close(); es = null; startPolling(); };
      } catch { startPolling(); }
    } else { startPolling(); }
    return () => { if (es) es.close(); if (poll) clearInterval(poll); };
  }, [refreshCount]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      setNq(""); setVisible(8);
      try {
        const r = await api.get("/notifications");
        setItems(r.data);
        await api.post("/notifications/read");
        setCount(0);
      } catch { /* ignore */ }
    }
  };

  const clearAll = async () => { await api.delete("/notifications").catch(() => {}); setItems([]); setCount(0); };
  const go = (n) => { setOpen(false); if (n.client_id) navigate(`/clients/${n.client_id}`); };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid={testId}>
          <Bell className="h-[18px] w-[18px]" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">{count > 9 ? "9+" : count}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-card/95 backdrop-blur-xl border-border" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display font-semibold text-sm">Notifications</span>
          {items.length > 0 && <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid="clear-notifications"><Trash2 className="h-3.5 w-3.5" /> Clear</button>}
        </div>
        {items.length > 0 && (
          <div className="px-3 py-2 border-b border-border/60">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={nq} onChange={(e) => { setNq(e.target.value); setVisible(8); }} placeholder="Search notifications…" className="pl-8 h-8 text-xs" data-testid="notifications-search" />
            </div>
          </div>
        )}
        {(() => {
          const q = nq.toLowerCase();
          const filtered = items.filter((n) => (n.message || "").toLowerCase().includes(q));
          const shown = filtered.slice(0, visible);
          return (
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No notifications yet.</p>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No matches.</p>
              ) : (
                <>
                  {shown.map((n) => {
                    const done = n.type === "onboarding_complete";
                    const Icon = done ? CheckCircle2 : FileSignature;
                    return (
                      <button key={n.id} onClick={() => go(n)} className="w-full text-left flex gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-border/50" data-testid={`notification-${n.id}`}>
                        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${done ? "text-emerald-400" : "text-primary"}`} />
                        <div className="min-w-0">
                          <div className="text-sm leading-snug">{n.message}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                        </div>
                      </button>
                    );
                  })}
                  {filtered.length > visible && (
                    <button onClick={() => setVisible((v) => v + 8)} className="w-full text-xs text-primary hover:bg-white/5 py-3 transition-colors" data-testid="notifications-load-more">Load more ({filtered.length - visible})</button>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </PopoverContent>
    </Popover>
  );
}
